import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, Clock, Gift, Users, Star, Zap, ArrowRight, X } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

// Launch date: January 31, 2026 at 00:00:00 CET
const LAUNCH_DATE = new Date('2026-01-31T00:00:00+01:00');

// Discount tiers based on time until launch
const getDiscountTier = () => {
  const now = new Date();
  const daysUntilLaunch = Math.ceil((LAUNCH_DATE - now) / (1000 * 60 * 60 * 24));
  
  if (now >= LAUNCH_DATE) {
    return null; // Launch has happened
  }
  
  // Calculate which tier we're in
  // Assuming ~60 days before launch for the campaign
  if (daysUntilLaunch > 45) {
    return {
      name: 'Founding Member',
      discount: 20,
      badge: '🏆',
      color: 'from-yellow-500 to-amber-600',
      borderColor: 'border-yellow-500',
      bgColor: 'bg-yellow-500/20',
      textColor: 'text-yellow-400',
      description: 'Exklusivster Rabatt für Gründungsmitglieder'
    };
  } else if (daysUntilLaunch > 21) {
    return {
      name: 'Early Bird',
      discount: 15,
      badge: '🐦',
      color: 'from-cyan-500 to-blue-600',
      borderColor: 'border-cyan-500',
      bgColor: 'bg-cyan-500/20',
      textColor: 'text-cyan-400',
      description: 'Früh dabei sein lohnt sich'
    };
  } else {
    return {
      name: 'Last Chance',
      discount: 10,
      badge: '⏰',
      color: 'from-red-500 to-orange-600',
      borderColor: 'border-red-500',
      bgColor: 'bg-red-500/20',
      textColor: 'text-red-400',
      description: 'Letzte Chance vor dem Launch!'
    };
  }
};

export const LaunchCountdown = ({ variant = 'banner', onClose }) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isVisible, setIsVisible] = useState(true);
  const [discountTier, setDiscountTier] = useState(getDiscountTier());

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const difference = LAUNCH_DATE - now;

      if (difference <= 0) {
        setIsVisible(false);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    };

    setTimeLeft(calculateTimeLeft());
    setDiscountTier(getDiscountTier());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
      setDiscountTier(getDiscountTier());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Don't render if launch has passed
  if (!isVisible || !discountTier) return null;

  const TimeBlock = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <motion.div
        key={value}
        initial={{ scale: 1.2, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="relative"
      >
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 flex items-center justify-center">
          <span className="text-2xl md:text-3xl font-bold text-white font-mono">
            {String(value).padStart(2, '0')}
          </span>
        </div>
        {/* Glowing effect */}
        <div className={`absolute inset-0 rounded-xl bg-gradient-to-r ${discountTier.color} opacity-20 blur-xl`} />
      </motion.div>
      <span className="text-xs text-gray-400 mt-2 uppercase tracking-wider">{label}</span>
    </div>
  );

  // Full-page hero variant
  if (variant === 'hero') {
    return (
      <section className="relative py-16 px-6 overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F14] via-[#7CD4FD]/5 to-[#0B0F14]" />
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#7CD4FD]/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#22C55E]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-5xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            {/* Launch Badge */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r ${discountTier.color} mb-8`}
            >
              <Rocket className="w-5 h-5 text-white animate-bounce" />
              <span className="text-white font-bold text-lg">LAUNCH: 31. JANUAR 2026</span>
              <Rocket className="w-5 h-5 text-white animate-bounce" style={{ animationDelay: '0.5s' }} />
            </motion.div>

            {/* Headline */}
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-white">Der Countdown läuft!</span>
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Sichern Sie sich jetzt Ihren <span className={`font-bold ${discountTier.textColor}`}>{discountTier.name}</span> Status 
              mit <span className={`font-bold ${discountTier.textColor}`}>{discountTier.discount}% Rabatt</span> – 
              lebenslang gültig solange Ihr Abo aktiv ist!
            </p>

            {/* Countdown Timer */}
            <div className="flex justify-center gap-4 md:gap-6 mb-10">
              <TimeBlock value={timeLeft.days} label="Tage" />
              <TimeBlock value={timeLeft.hours} label="Stunden" />
              <TimeBlock value={timeLeft.minutes} label="Minuten" />
              <TimeBlock value={timeLeft.seconds} label="Sekunden" />
            </div>

            {/* Current Discount Tier Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className={`max-w-lg mx-auto p-6 rounded-2xl ${discountTier.bgColor} border-2 ${discountTier.borderColor} mb-8`}
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-4xl">{discountTier.badge}</span>
                <div>
                  <h3 className={`text-2xl font-bold ${discountTier.textColor}`}>{discountTier.name}</h3>
                  <p className="text-gray-400 text-sm">{discountTier.description}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 rounded-lg bg-black/30">
                  <Gift className={`w-6 h-6 mx-auto mb-1 ${discountTier.textColor}`} />
                  <p className="text-white font-bold">{discountTier.discount}%</p>
                  <p className="text-xs text-gray-400">Rabatt</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-black/30">
                  <Star className="w-6 h-6 mx-auto mb-1 text-yellow-400" />
                  <p className="text-white font-bold">Lebenslang</p>
                  <p className="text-xs text-gray-400">Gültig</p>
                </div>
                <div className="text-center p-3 rounded-lg bg-black/30 relative group">
                  <Users className="w-6 h-6 mx-auto mb-1 text-purple-400" />
                  <p className="text-white font-bold">+5%</p>
                  <p className="text-xs text-gray-400">Referral*</p>
                  {/* Tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 rounded-lg text-xs text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 border border-gray-700">
                    *Nur solange Werber aktives Abo hat
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate('/signup?earlybird=true')}
                className={`w-full bg-gradient-to-r ${discountTier.color} hover:opacity-90 text-white font-bold py-4 text-lg rounded-xl`}
              >
                Jetzt {discountTier.discount}% sichern
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>

            {/* Benefits List */}
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span className="text-gray-300">Exklusiver Gründungsmitglied-Badge</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <Gift className="w-4 h-4 text-green-400" />
                <span className="text-gray-300">Rabatt gilt lebenslang</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
                <Users className="w-4 h-4 text-blue-400" />
                <span className="text-gray-300">+5% Referral (bei aktivem Werber-Abo)</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  // Compact banner variant (for top of page)
  if (variant === 'banner') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className={`relative bg-gradient-to-r ${discountTier.color} overflow-hidden`}
        >
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{discountTier.badge}</span>
                <div className="text-white">
                  <span className="font-bold">{discountTier.name}:</span>
                  <span className="ml-2">{discountTier.discount}% Rabatt – noch</span>
                  <span className="font-mono font-bold ml-2">
                    {timeLeft.days}T {timeLeft.hours}h {timeLeft.minutes}m
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  onClick={() => navigate('/signup?earlybird=true')}
                  size="sm"
                  className="bg-white text-gray-900 hover:bg-gray-100 font-bold"
                >
                  Jetzt sichern
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
                {onClose && (
                  <button onClick={onClose} className="text-white/80 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          </div>
          
          {/* Animated shine effect */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute inset-0 -translate-x-full animate-shine bg-gradient-to-r from-transparent via-white/20 to-transparent" />
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return null;
};

// Export the discount tier getter for use in other components
export { getDiscountTier, LAUNCH_DATE };

export default LaunchCountdown;
